import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  fetchAnalyticsOverview,
  fetchBranchComparison,
  fetchIncomeChart,
  fetchPosDailyIncome,
  fetchTopProducts,
  fetchTopServices,
} from '../../api/analytics';
import { fetchBranches } from '../../api/salon';
import ExportReportModal from '../../components/analytics/ExportReportModal';
import DashboardShell from '../../components/ui/DashboardShell';
import { useTheme } from '../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';

const TEAL = '#14b8a6';
const CORAL = '#fb7185';
const PURPLE = '#a855f7';
const PIE_COLORS = ['#14b8a6', '#fb7185', '#a855f7', '#38bdf8', '#fbbf24'];

const formatLkr = (n) => `LKR ${Number(n || 0).toLocaleString()}`;

const Trend = ({ value }) => {
  const { t } = useTranslation();
  const positive = value >= 0;
  return (
    <span className={positive ? 'text-emerald-400' : 'text-red-400'}>
      {t('salon.analytics.trendVsLast', { direction: positive ? '↑' : '↓', value: Math.abs(value) })}
    </span>
  );
};

const EmptyDonut = ({ title }) => {
  const { t } = useTranslation();
  return (
    <div className="flex h-64 flex-col items-center justify-center rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white/50 dark:bg-zinc-900/50">
      <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{title}</p>
      <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-500">{t('salon.analytics.noData')}</p>
    </div>
  );
};

const SalonAnalyticsPage = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const chartTheme = getChartTheme(theme);
  const now = new Date();
  const [branchId, setBranchId] = useState('all');
  const [period, setPeriod] = useState('monthly');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [incomeType, setIncomeType] = useState('all');
  const [exportOpen, setExportOpen] = useState(false);

  const dateRange = useMemo(() => {
    if (period === 'yearly') {
      return {
        startDate: `${year - 4}-01-01`,
        endDate: `${year}-12-31`,
      };
    }
    if (period === 'monthly') {
      return {
        startDate: `${year}-01-01`,
        endDate: `${year}-12-31`,
      };
    }
    const lastDay = new Date(year, month, 0).getDate();
    const m = String(month).padStart(2, '0');
    return {
      startDate: `${year}-${m}-01`,
      endDate: `${year}-${m}-${String(lastDay).padStart(2, '0')}`,
    };
  }, [period, year, month]);

  const branchParam = branchId === 'all' ? 'all' : branchId;

  const { data: branchData } = useQuery({
    queryKey: ['branches'],
    queryFn: fetchBranches,
  });
  const branches = branchData?.branches || [];

  const analyticsStale = 5 * 60 * 1000;

  const { data: overview } = useQuery({
    queryKey: ['analytics-overview', branchParam, dateRange],
    queryFn: () =>
      fetchAnalyticsOverview({
        branchId: branchParam,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      }),
    staleTime: analyticsStale,
  });

  const { data: chartData } = useQuery({
    queryKey: ['income-chart', period, branchParam, incomeType, year, month],
    queryFn: () =>
      fetchIncomeChart({
        period,
        branchId: branchParam,
        type: incomeType,
        year,
        month,
      }),
    staleTime: analyticsStale,
  });

  const { data: branchComp } = useQuery({
    queryKey: ['branch-comparison'],
    queryFn: fetchBranchComparison,
    staleTime: analyticsStale,
  });

  const { data: topServicesData } = useQuery({
    queryKey: ['top-services', branchParam, dateRange],
    queryFn: () =>
      fetchTopServices({
        branchId: branchParam,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      }),
    staleTime: analyticsStale,
  });

  const { data: topProductsData } = useQuery({
    queryKey: ['top-products', branchParam, dateRange],
    queryFn: () =>
      fetchTopProducts({
        branchId: branchParam,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      }),
    staleTime: analyticsStale,
  });

  const { data: posDailyData } = useQuery({
    queryKey: ['pos-daily-income', branchParam, dateRange],
    queryFn: () =>
      fetchPosDailyIncome({
        branchId: branchParam,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      }),
    staleTime: analyticsStale,
  });

  const series = chartData?.series || [];
  const posDailyRows = posDailyData?.daily || [];
  const branchRows = (branchComp?.branches || []).map((b) => ({
    name: b.branchName,
    revenue: b.revenue,
    fill: b.isAboveAverage ? '#22c55e' : CORAL,
    isAboveAverage: b.isAboveAverage,
  }));
  const avgRevenue = branchComp?.branches?.[0]?.averageRevenue ?? 0;

  const servicePie = (topServicesData?.services || []).map((s) => ({
    name: s.serviceName,
    value: s.revenue,
  }));
  const productPie = (topProductsData?.products || []).map((p) => ({
    name: p.productName,
    value: p.revenue,
  }));

  const summaryCards = [
    {
      label: t('salon.analytics.totalRevenue'),
      value: formatLkr(overview?.totalRevenue),
      trend: overview?.revenueChange,
      icon: '💰',
    },
    {
      label: t('salon.analytics.posRevenue'),
      value: formatLkr(overview?.posRevenue),
      trend: overview?.posRevenueChange,
      icon: '🧾',
      sub: t('salon.analytics.posSalesSub', { count: overview?.posSalesCount ?? 0 }),
    },
    {
      label: t('salon.analytics.totalDiscounts'),
      value: formatLkr(overview?.totalDiscountsGiven),
      icon: '🏷️',
      sub: t('salon.analytics.discountsSub'),
    },
    {
      label: t('salon.analytics.totalAppointments'),
      value: overview?.totalAppointments ?? 0,
      trend: overview?.appointmentChange,
      icon: '📅',
    },
    {
      label: t('salon.analytics.totalOrders'),
      value: overview?.totalOrders ?? 0,
      trend: overview?.orderChange,
      icon: '📦',
    },
    {
      label: t('salon.analytics.activeClients'),
      value: overview?.activeClients ?? 0,
      trend: overview?.activeClientChange,
      icon: '👥',
      sub: t('salon.analytics.newClientsSub', { count: overview?.newClientsThisPeriod ?? 0 }),
    },
  ];

  return (
    <DashboardShell title={t('salon.analytics.title')} subtitle={t('salon.analytics.subtitle')}>
      <div className="sticky top-0 z-20 -mx-2 mb-6 space-y-3 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50/95 dark:bg-zinc-950/95 p-4 backdrop-blur">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setBranchId('all')}
            className={`rounded-lg px-3 py-1.5 text-xs ${branchId === 'all' ? 'bg-glow-600/30 text-glow-300' : 'bg-zinc-100 dark:bg-zinc-800'}`}
          >
            {t('salon.analytics.allBranches')}
          </button>
          {branches.map((b) => (
            <button
              key={b._id}
              type="button"
              onClick={() => setBranchId(b._id)}
              className={`rounded-lg px-3 py-1.5 text-xs ${branchId === b._id ? 'bg-glow-600/30 text-glow-300' : 'bg-zinc-100 dark:bg-zinc-800'}`}
            >
              {b.name}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {['weekly', 'monthly', 'yearly'].map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              className={`rounded-lg px-3 py-1.5 text-xs capitalize ${period === p ? 'bg-glow-600/30 text-glow-300' : 'bg-zinc-100 dark:bg-zinc-800'}`}
            >
              {t(`salon.analytics.period${p.charAt(0).toUpperCase()}${p.slice(1)}`)}
            </button>
          ))}

          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1.5 text-xs"
          >
            {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          {period === 'weekly' && (
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1.5 text-xs"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          )}

          {['all', 'services', 'products', 'pos'].map((typeKey) => (
            <button
              key={typeKey}
              type="button"
              onClick={() => setIncomeType(typeKey)}
              className={`rounded-lg px-3 py-1.5 text-xs capitalize ${incomeType === typeKey ? 'bg-teal-900/40 text-teal-300' : 'bg-zinc-100 dark:bg-zinc-800'}`}
            >
              {typeKey === 'all'
                ? t('salon.analytics.incomeAll')
                : typeKey === 'pos'
                  ? t('salon.analytics.incomePos')
                  : t(`salon.analytics.income${typeKey.charAt(0).toUpperCase()}${typeKey.slice(1)}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {summaryCards.map((card) => (
          <div key={card.label} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
            <div className="flex items-start justify-between">
              <p className="text-xs text-zinc-600 dark:text-zinc-500">{card.label}</p>
              <span className="text-lg">{card.icon}</span>
            </div>
            <p className="mt-2 text-2xl font-semibold">{card.value}</p>
            <p className="mt-2 text-xs">
              {card.trend != null && <Trend value={card.trend} />}
              {card.sub && <span className="mt-1 block text-zinc-600 dark:text-zinc-500">{card.sub}</span>}
            </p>
          </div>
        ))}
      </div>

      <div className="mb-8 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
        <h3 className="mb-4 font-medium">{t('salon.analytics.incomeOverTime')}</h3>
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="tealFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={TEAL} stopOpacity={0.4} />
                <stop offset="95%" stopColor={TEAL} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="coralFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={CORAL} stopOpacity={0.4} />
                <stop offset="95%" stopColor={CORAL} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="purpleFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={PURPLE} stopOpacity={0.4} />
                <stop offset="95%" stopColor={PURPLE} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} />
            <XAxis dataKey="label" tick={{ fill: chartTheme.tick, fontSize: 12 }} />
            <YAxis tick={{ fill: chartTheme.tick, fontSize: 12 }} tickFormatter={(v) => v.toLocaleString()} />
            <Tooltip
              contentStyle={{
                background: chartTheme.tooltipBg,
                border: `1px solid ${chartTheme.tooltipBorder}`,
                color: chartTheme.tooltipColor,
              }}
              formatter={(value, name) => {
                const labels = {
                  serviceRevenue: t('salon.analytics.chartServices'),
                  productRevenue: t('salon.analytics.chartProducts'),
                  posRevenue: t('salon.analytics.chartPos'),
                  total: t('salon.analytics.chartTotal'),
                };
                return [formatLkr(value), labels[name] || name];
              }}
              labelFormatter={(label) => label}
            />
            {(incomeType === 'all' || incomeType === 'services') && (
              <Area
                type="monotone"
                dataKey="serviceRevenue"
                stroke={TEAL}
                fill="url(#tealFill)"
                animationDuration={800}
                name={t('salon.analytics.chartServices')}
              />
            )}
            {(incomeType === 'all' || incomeType === 'products') && (
              <Area
                type="monotone"
                dataKey="productRevenue"
                stroke={CORAL}
                fill="url(#coralFill)"
                animationDuration={800}
                name={t('salon.analytics.chartProducts')}
              />
            )}
            {(incomeType === 'all' || incomeType === 'pos') && (
              <Area
                type="monotone"
                dataKey="posRevenue"
                stroke={PURPLE}
                fill="url(#purpleFill)"
                animationDuration={800}
                name={t('salon.analytics.chartPos')}
              />
            )}
            <Legend />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="mb-8 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
        <h3 className="mb-4 font-medium">{t('salon.analytics.branchPerformance')}</h3>
        {branchRows.length === 0 ? (
          <p className="text-sm text-zinc-600 dark:text-zinc-500">{t('salon.analytics.noBranchData')}</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(200, branchRows.length * 48)}>
            <BarChart data={branchRows} layout="vertical" margin={{ left: 20, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} />
              <XAxis type="number" tick={{ fill: chartTheme.tick, fontSize: 12 }} tickFormatter={(v) => v.toLocaleString()} />
              <YAxis type="category" dataKey="name" width={100} tick={{ fill: chartTheme.tick, fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  background: chartTheme.tooltipBg,
                  border: `1px solid ${chartTheme.tooltipBorder}`,
                  color: chartTheme.tooltipColor,
                }}
                formatter={(value, _name, props) => [
                  formatLkr(value),
                  props.payload.isAboveAverage ? t('salon.analytics.aboveAverage') : t('salon.analytics.belowAverage'),
                ]}
              />
              <ReferenceLine x={avgRevenue} stroke={chartTheme.tick} strokeDasharray="4 4" />
              <Bar dataKey="revenue" radius={[0, 4, 4, 0]} animationDuration={800}>
                {branchRows.map((entry) => (
                  <Cell key={entry.name} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="mb-8 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-medium">{t('salon.analytics.posDailyIncome')}</h3>
            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-500">{t('salon.analytics.posDailySub')}</p>
          </div>
          <p className="text-sm font-medium text-purple-400">
            {t('salon.analytics.posDailyTotal', {
              amount: formatLkr(posDailyData?.totalRevenue),
              count: posDailyData?.totalSales ?? 0,
            })}
          </p>
        </div>
        {posDailyRows.length === 0 ? (
          <p className="text-sm text-zinc-600 dark:text-zinc-500">{t('salon.analytics.noPosData')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[320px] text-left text-sm">
              <thead>
                <tr className="border-b border-zinc-200 dark:border-zinc-800 text-xs text-zinc-600 dark:text-zinc-500">
                  <th className="py-2 pr-4 font-medium">{t('salon.analytics.posDailyDate')}</th>
                  <th className="py-2 pr-4 font-medium">{t('salon.analytics.posDailySales')}</th>
                  <th className="py-2 font-medium">{t('salon.analytics.posDailyRevenue')}</th>
                </tr>
              </thead>
              <tbody>
                {posDailyRows.map((row) => (
                  <tr key={row.date} className="border-b border-zinc-100 dark:border-zinc-800/80">
                    <td className="py-2.5 pr-4">{row.date}</td>
                    <td className="py-2.5 pr-4">{row.salesCount}</td>
                    <td className="py-2.5">{formatLkr(row.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mb-8 grid gap-6 sm:grid-cols-1 lg:grid-cols-2">
        {servicePie.length === 0 ? (
          <EmptyDonut title={t('salon.analytics.topServicesByRevenue')} />
        ) : (
          <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
            <h3 className="mb-4 font-medium">{t('salon.analytics.topServicesByRevenue')}</h3>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={servicePie}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  animationDuration={800}
                >
                  {servicePie.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatLkr(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {productPie.length === 0 ? (
          <EmptyDonut title={t('salon.analytics.topProductsByRevenue')} />
        ) : (
          <div className="hidden rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5 sm:block">
            <h3 className="mb-4 font-medium">{t('salon.analytics.topProductsByRevenue')}</h3>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={productPie}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  animationDuration={800}
                >
                  {productPie.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatLkr(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6 text-center">
        <p className="text-sm text-zinc-600 dark:text-zinc-400">{t('salon.analytics.exportHint')}</p>
        <button
          type="button"
          onClick={() => setExportOpen(true)}
          className="mt-4 rounded-lg bg-glow-600 px-6 py-2.5 text-sm font-medium text-white"
        >
          {t('salon.analytics.exportButton')}
        </button>
      </div>

      <ExportReportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        branches={branches}
        defaultBranchId={branchId}
      />
    </DashboardShell>
  );
};

export default SalonAnalyticsPage;
